import { ChangeEvent } from 'react';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Select } from '@/components/ui/daisy/select';
import { TextInput } from '@/components/ui/daisy/textinput';

import {
  DiscreteDateInputProps,
  DiscreteValue,
  TIME_DIRECTIONS,
  TIME_GRANULARITIES,
  TimeDirection,
  TimeGranularity,
} from './types';
import { useDiscreteLabels } from './useDiscreteLabels';

export const DiscreteDateInput = ({
  value,
  onChange,
  disabled = false,
  size = 'sm',
  labels: labelsProp,
}: DiscreteDateInputProps) => {
  const defaultLabels = useDiscreteLabels();
  const labels = labelsProp ?? defaultLabels;
  const direction = value?.direction ?? 'past';
  const granularity = value?.granularity ?? 'day';
  const amount = value?.amount;

  const showAmountInput = direction !== 'this';

  const emitChange = (partial: Partial<DiscreteValue>) => {
    const next: DiscreteValue = { direction, granularity, amount, ...partial };
    onChange({
      ...next,
      amount: next.direction === 'this' ? undefined : next.amount,
    });
  };

  const emitSelectChange = (partial: Partial<DiscreteValue>) =>
    emitChange({ amount: amount ?? 1, ...partial });

  const handleAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
    const numericAmount = event.target.valueAsNumber;
    const isValidAmount = !Number.isNaN(numericAmount) && numericAmount >= 1;
    emitChange({ amount: isValidAmount ? numericAmount : undefined });
  };

  return (
    <div className="flex items-center gap-2">
      <Select
        aria-label={labels.directionAriaLabel}
        size={size}
        value={direction}
        disabled={disabled}
        onChange={event =>
          emitSelectChange({ direction: event.target.value as TimeDirection })
        }
      >
        {TIME_DIRECTIONS.map(timeDirection => (
          <Select.Option key={timeDirection} value={timeDirection}>
            {labels.directions[timeDirection]}
          </Select.Option>
        ))}
      </Select>

      <ConditionalRenderer condition={showAmountInput}>
        <TextInput
          type="number"
          aria-label={labels.amountAriaLabel}
          size={size}
          min={1}
          max={999}
          className="w-20"
          value={amount ?? ''}
          disabled={disabled}
          onChange={handleAmountChange}
        />
      </ConditionalRenderer>

      <Select
        aria-label={labels.granularityAriaLabel}
        size={size}
        value={granularity}
        disabled={disabled}
        onChange={event =>
          emitSelectChange({
            granularity: event.target.value as TimeGranularity,
          })
        }
      >
        {TIME_GRANULARITIES.map(timeGranularity => (
          <Select.Option key={timeGranularity} value={timeGranularity}>
            {labels.granularities[timeGranularity]}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
};

DiscreteDateInput.displayName = 'ComplexDateInput.DiscreteDateInput';
